import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MapLayer, TileLayer } from '@/lib/leaflet/leafletMap';

export type MapLayerControlProps = {
  tileLayers?: readonly TileLayer[];
  overlayLayers?: readonly MapLayer[];
  activeTileLayerId?: string;
  onTileLayerChanged?: (layerId: string) => void | Promise<void>;
  visibleOverlayIds?: ReadonlySet<string>;
  onOverlayToggled?: (id: string, visible: boolean) => void | Promise<void>;
};

function getThumbnailUrl(layerId: string): string {
  switch (layerId.toLowerCase()) {
    case 'mapycz-basic':
      return '/Images/map-thumbnails/basic.png';
    case 'mapycz-aerial':
      return '/Images/map-thumbnails/aerial.png';
    case 'mapycz-outdoor':
      return '/Images/map-thumbnails/outdoor.png';
    case 'mapycz-winter':
      return '/Images/map-thumbnails/winter.png';
    default:
      return '/Images/map-thumbnails/basic.png';
  }
}

function formatColor(color: string): string {
  if (!color) {
    return '#ccc';
  }
  if (color.startsWith('#')) {
    return color;
  }
  if (color.length === 8) {
    return '#' + color.substring(0, 6);
  }
  return '#' + color;
}

export function MapLayerControl({
  tileLayers = [],
  overlayLayers = [],
  activeTileLayerId = '',
  onTileLayerChanged,
  visibleOverlayIds = new Set<string>(),
  onOverlayToggled,
}: MapLayerControlProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const editMapLabel = t('Map_LayerControl_EditMap');

  const selectTileLayer = async (layerId: string) => {
    if (layerId !== activeTileLayerId && onTileLayerChanged) {
      await onTileLayerChanged(layerId);
    }
  };

  return (
    <div className="map-layer-control z-1">
      <button
        type="button"
        className="map-overlay-control btn bg-white border border-dark rounded-3 shadow d-flex align-items-center gap-2 px-2 py-1"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-label={editMapLabel}
      >
        <i className="fa-solid fa-layer-group"></i>
        <span className="map-overlay-control__text fw-medium">{editMapLabel}</span>
        <i className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} fa-xs`}></i>
      </button>

      {isExpanded && (
        <div className="map-layer-control__panel bg-white border border-dark rounded-3 shadow position-absolute mt-1">
          {tileLayers.length > 0 && (
            <div className="py-2 border-bottom">
              <div className="map-layer-control__section-title px-3 pb-2 text-muted small fw-semibold text-uppercase">
                {t('Map_LayerControl_BaseMapTitle')}
              </div>
              {tileLayers.map((layer) => (
                <div
                  key={layer.id}
                  className={`map-layer-control__base-layer d-flex align-items-center gap-2 px-3 py-1 ${layer.id === activeTileLayerId ? 'bg-primary bg-opacity-10' : ''}`}
                  onClick={() => void selectTileLayer(layer.id)}
                >
                  <img className="map-layer-control__thumbnail rounded" src={getThumbnailUrl(layer.id)} alt={layer.name} />
                  <span className="flex-grow-1">{layer.name}</span>
                  {layer.id === activeTileLayerId && (
                    <span className="map-layer-control__active-indicator rounded-circle"></span>
                  )}
                </div>
              ))}
            </div>
          )}

          {overlayLayers.length > 0 && (
            <div className="py-2">
              <div className="map-layer-control__section-title px-3 pb-2 text-muted small fw-semibold text-uppercase">
                {t('Map_LayerControl_OverlayLayersTitle')}
              </div>
              {overlayLayers.map((layer) => (
                <div key={layer.id} className="d-flex align-items-center justify-content-between px-3 py-2">
                  <label htmlFor={`overlay-${layer.id}`} style={{ cursor: 'pointer' }} className="d-flex align-items-center gap-2">
                    <span
                      className="map-layer-control__overlay-indicator rounded"
                      style={{ backgroundColor: formatColor(layer.borderColor) }}
                    ></span>
                    <span>{layer.title}</span>
                  </label>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      id={`overlay-${layer.id}`}
                      type="checkbox"
                      role="switch"
                      checked={visibleOverlayIds.has(layer.id)}
                      onChange={(event) => void onOverlayToggled?.(layer.id, event.target.checked)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
